import React, { useState } from 'react';

const botPersonalities = {
  curiosityBot: {
    name: 'Curio',
    color: 'blue',
    description: 'Helps explore different perspectives and asks thoughtful questions',
    icon: '🔍',
    tagline: 'Let\'s explore together'
  },
  empathyBot: {
    name: 'Empathy',
    color: 'purple',
    description: 'Focuses on understanding emotions and building connections',
    icon: '💝',
    tagline: 'Your feelings matter'
  },
  wisdomBot: {
    name: 'Wisdom',
    color: 'green',
    description: 'Provides balanced insights and helps find common ground',
    icon: '🌱',
    tagline: 'Growing understanding'
  },
  peaceBot: {
    name: 'Peace',
    color: 'yellow',
    description: 'Helps maintain calm and respectful dialogue',
    icon: '🕊️',
    tagline: 'Calm waters run deep'
  }
};

const comments = [
  {
    id: 1,
    username: 'JordanR202',
    isBot: false,
    content: "I've been thinking about how we can bridge our differences. What if we started by understanding each other's core values?",
    role: 'user',
    timestamp: '2 minutes ago'
  },
  {
    id: 2,
    username: 'Curio (YBot)',
    isBot: true,
    content: "That's a thoughtful approach! What values do you think are most important in this conversation?",
    role: 'curiosityBot',
    timestamp: '1 minute ago'
  },
  {
    id: 3,
    username: 'Casey88',
    isBot: false,
    content: "I'm frustrated because it feels like no one is listening to the scientific evidence. How can we have a real discussion when facts are ignored?",
    role: 'user',
    timestamp: 'Just now'
  },
];

const getRewordedText = (text) => {
  return text
    .replace("frustrated", "passionate")
    .replace("no one is listening", "I feel unheard")
    .replace("facts are ignored", "scientific perspectives aren't being considered");
};

const getBotResponse = (comment, botType) => {
  const responses = {
    curiosityBot: [
      "What specific evidence or experiences have shaped your perspective on this?",
      "How might we explore this topic from different angles to find common understanding?",
      "What questions would help us better understand each other's viewpoints?"
    ],
    empathyBot: [
      "I hear your passion for this topic. What makes this particularly important to you?",
      "It sounds like you're feeling unheard. How might we create space for everyone's voice?",
      "Your concern about scientific perspectives is valid. How can we ensure all viewpoints are respectfully considered?"
    ],
    wisdomBot: [
      "Let's consider both the scientific evidence and the human experiences involved here.",
      "What common goals might we share, even with different perspectives on the science?",
      "How can we balance scientific understanding with practical solutions that work for everyone?"
    ],
    peaceBot: [
      "Let's take a deep breath and approach this with open minds and hearts.",
      "How can we discuss scientific evidence while maintaining respect for all perspectives?",
      "What language might help us communicate our views more constructively?"
    ]
  };
  
  return responses[botType][Math.floor(Math.random() * responses[botType].length)];
};

export default function ConversationThread() {
  const [rewordedComment, setRewordedComment] = useState(null);
  const [activeBots, setActiveBots] = useState(['curiosityBot']);
  const [newComment, setNewComment] = useState('');

  const handleReword = (comment) => {
    const newText = getRewordedText(comment.content);
    setRewordedComment({ id: comment.id, text: newText });
  };

  const handleAddBot = (botType) => {
    if (!activeBots.includes(botType)) {
      setActiveBots([...activeBots, botType]);
    }
  };

  const handleRemoveBot = (botType) => {
    setActiveBots(activeBots.filter(bot => bot !== botType));
  };

  const handleSubmitComment = () => {
    if (newComment.trim()) {
      const newUserComment = {
        id: comments.length + 1,
        username: 'You',
        isBot: false,
        content: newComment,
        role: 'user',
        timestamp: 'Just now'
      };
      
      comments.push(newUserComment);
      
      // Get responses from active bots
      activeBots.forEach(botType => {
        const botResponse = {
          id: comments.length + 1,
          username: `${botPersonalities[botType].name} (YBot)`,
          isBot: true,
          content: getBotResponse(newUserComment, botType),
          role: botType,
          timestamp: 'Just now'
        };
        comments.push(botResponse);
      });
      
      setNewComment('');
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto bg-white rounded-xl shadow-md space-y-4">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">YconnX Thread</h2>
        <span className="text-sm text-gray-500">Healthy Dialogue Space</span>
      </div>
      
      {/* Bot Selection Panel */}
      <div className="bg-gray-50 p-4 rounded-lg mb-4 border border-gray-200">
        <h3 className="text-lg font-semibold mb-3 text-gray-700">Your Conversation Partners</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(botPersonalities).map(([type, bot]) => (
            <button
              key={type}
              onClick={() => activeBots.includes(type) ? handleRemoveBot(type) : handleAddBot(type)}
              className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm transition-all duration-200 ${
                activeBots.includes(type)
                  ? `bg-${bot.color}-100 text-${bot.color}-700 border-${bot.color}-300 shadow-sm`
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span className="text-lg">{bot.icon}</span>
              <span>{bot.name}</span>
              {activeBots.includes(type) && (
                <span className="text-xs opacity-75">{bot.tagline}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Conversation Thread */}
      <div className="space-y-4">
        {comments.map((comment) => {
          const isReworded = rewordedComment && rewordedComment.id === comment.id;
          const botInfo = comment.isBot ? botPersonalities[comment.role] : null;
          
          return (
            <div 
              key={comment.id} 
              className={`border rounded-lg p-4 relative transition-all duration-200 ${
                comment.isBot 
                  ? `bg-${botInfo?.color}-50 border-${botInfo?.color}-200` 
                  : 'bg-white border-gray-200'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-semibold ${
                    comment.isBot ? `text-${botInfo?.color}-700` : 'text-gray-700'
                  }`}>
                    {comment.username}
                  </span>
                  {comment.isBot && (
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      `bg-${botInfo?.color}-100 text-${botInfo?.color}-700`
                    }`}>
                      {botInfo?.icon} {botInfo?.name}
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500">{comment.timestamp}</span>
              </div>
              <p className={`mt-2 ${
                comment.isBot ? `text-${botInfo?.color}-800` : 'text-gray-800'
              }`}>
                {isReworded ? rewordedComment.text : comment.content}
              </p>
              <div className="flex gap-4 mt-3 text-sm">
                <button 
                  className={`hover:text-${botInfo?.color || 'blue'}-600 transition-colors duration-200`}
                  onClick={() => handleReword(comment)}
                >
                  Reword
                </button>
                <button className="hover:text-green-600 transition-colors duration-200">
                  Thank
                </button>
                <button className="hover:text-yellow-600 transition-colors duration-200">
                  Highlight
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* New Comment Input */}
      <div className="mt-6">
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Share your thoughts... Let's build understanding together."
          className="w-full p-4 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
          rows={3}
        />
        <button
          onClick={handleSubmitComment}
          className="mt-3 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-sm"
        >
          Send Message
        </button>
      </div>
    </div>
  );
}
